import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import { organizationsService } from '@/services/organizations/organizationsService';
import toast from 'react-hot-toast';
import type {
  ImportDefinition,
  AllowedTable,
  TableField,
  FilePreview,
  FilePreviewColumn,
  SourcePreviewInfo,
  FtpFileInfo,
  ImportSource,
  FieldMapping,
  HistoryRun,
  DetailRun,
  ImportProgress,
  SourceFilesModalInfo,
  SourceFile,
  SchemaValidationError,
  HeaderCellBinding,
} from './types';
import {
  AUTO_MAP_TARGET_ALIASES,
  normalizeHeaderName,
  containsAnyToken,
  previewMappedValueAsSaved,
} from './types';
import {
  applyHeaderCellsToPreview,
  headerBindingColumnNames,
  headerBindingsToFieldPairs,
} from './headerCellsUtils';
import {
  type FuelCompanyRef,
  type FuelSupplierProfile,
  FUEL_TARGET_TABLE,
  buildProfileList,
  resolveActiveProfile,
  resolveFuelCompanyId,
  findProfileByKey,
} from './fuelSupplierProfiles';
import { FUEL_SUPPLIER_DISALLOWED_TARGETS } from './types';

export const useImportsManagement = () => {
  const { t } = useTranslation();

  // Core state
  const [definitions, setDefinitions] = useState<ImportDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Wizard state
  const [importName, setImportName] = useState('');
  const [importDescription, setImportDescription] = useState('');
  const [selectedTable, setSelectedTable] = useState('');
  const [allowedTables, setAllowedTables] = useState<AllowedTable[]>([]);
  const [importSources, setImportSources] = useState<ImportSource[]>([]);
  const [importSourcesLoading, setImportSourcesLoading] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [tableFields, setTableFields] = useState<TableField[]>([]);
  const [filePreview, setFilePreview] = useState<FilePreview | null>(null);
  const [selectedFormats, setSelectedFormats] = useState<string[] | null>(null);
  const [sourcePreviewInfo, setSourcePreviewInfo] = useState<SourcePreviewInfo | null>(null);
  const [sourcePreviewLoading, setSourcePreviewLoading] = useState(false);
  const [sourcePreviewError, setSourcePreviewError] = useState<string | null>(null);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [, setUploadedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [transformParamsText, setTransformParamsText] = useState<Record<string, string>>({});
  const [showFtpModal, setShowFtpModal] = useState(false);
  const [ftpPath, setFtpPath] = useState('');
  const [ftpFiles, setFtpFiles] = useState<FtpFileInfo[]>([]);
  const [ftpLoading, setFtpLoading] = useState(false);
  const [ftpError, setFtpError] = useState<string | null>(null);
  const [ftpDefinitionId, setFtpDefinitionId] = useState<number | null>(null);

  // Source files modal
  const [sourceFilesModal, setSourceFilesModal] = useState<SourceFilesModalInfo | null>(null);
  const [sourceFilesList, setSourceFilesList] = useState<SourceFile[]>([]);
  const [sourceFilesLoading, setSourceFilesLoading] = useState(false);
  const [sourceFilesImportingId, setSourceFilesImportingId] = useState<number | null>(null);
  const [sourceLatestImportingDefinitionId, setSourceLatestImportingDefinitionId] = useState<number | null>(null);
  const [sourcePendingImportingDefinitionId, setSourcePendingImportingDefinitionId] = useState<number | null>(null);

  // File settings
  const [decimalSeparator, setDecimalSeparator] = useState<',' | '.'>(',');
  const [hasHeader, setHasHeader] = useState(true);
  const [delimiter, setDelimiter] = useState(';');
  const [skipRows, setSkipRows] = useState(0);
  const [headerCells, setHeaderCells] = useState<HeaderCellBinding[]>([]);
  const [filenameIncludePatterns, setFilenameIncludePatterns] = useState<string[]>([]);
  const [encoding, setEncoding] = useState<string>('UTF-8');
  const [sheetName, setSheetName] = useState('');
  const [sheetIndex, setSheetIndex] = useState('');

  // Import settings
  const [duplicateAction, setDuplicateAction] = useState<'skip' | 'update' | 'upsert' | 'replace'>('skip');
  const [errorAction, setErrorAction] = useState<'skip' | 'abort'>('skip');

  // Schedule
  const [scheduleType, setScheduleType] = useState<'manual' | 'daily' | 'weekly' | 'monthly'>('manual');
  const [scheduleTime, setScheduleTime] = useState('06:00');
  const [scheduleDayOfWeek, setScheduleDayOfWeek] = useState(1);
  const [scheduleDayOfMonth, setScheduleDayOfMonth] = useState(1);
  const [scheduleCron, setScheduleCron] = useState('');
  const [fileSourcePath, setFileSourcePath] = useState('');
  const [stableFileCheckIntervalMs, setStableFileCheckIntervalMs] = useState(2000);
  const [stableFileStableForMs, setStableFileStableForMs] = useState(5000);
  const [stableFileTimeoutMs, setStableFileTimeoutMs] = useState(300000);
  const [retryMaxRetries, setRetryMaxRetries] = useState(3);
  const [retryInitialDelayMs, setRetryInitialDelayMs] = useState(60000);
  const [retryMaxDelayMs, setRetryMaxDelayMs] = useState(600000);
  const [retryBackoffMultiplier, setRetryBackoffMultiplier] = useState(2);

  // Preset selection
  const [selectedPreset, setSelectedPreset] = useState<string>('');
  const [runImportDefinitionId, setRunImportDefinitionId] = useState<number | null>(null);
  const [runImportDryRun, setRunImportDryRun] = useState(false);
  const [runImportRowLimit, setRunImportRowLimit] = useState<number | null>(null);
  const runImportInputRef = useRef<HTMLInputElement | null>(null);

  // History
  const [historyRuns, setHistoryRuns] = useState<HistoryRun[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilterDefinitionId, setHistoryFilterDefinitionId] = useState('');
  const [historyFilterStatus, setHistoryFilterStatus] = useState('');
  const [detailRunId, setDetailRunId] = useState<string | null>(null);
  const [detailRun, setDetailRun] = useState<DetailRun | null>(null);

  // Import progress (SSE)
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);

  // Schema validation errors
  const [schemaValidationErrors, setSchemaValidationErrors] = useState<SchemaValidationError[]>([]);

  // Live fuel companies (drives DB-correct fuel_company_id + dynamic presets)
  const [fuelCompanies, setFuelCompanies] = useState<FuelCompanyRef[]>([]);

  // ---------- Fuel-supplier helpers (config-driven; see fuelSupplierProfiles.ts) ----------
  const getFuelSupplierFingerprint = useCallback((): string => {
    return [selectedPreset, importName, importDescription, filePreview?.filename, sourcePreviewInfo?.originalFilename]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }, [selectedPreset, importName, importDescription, filePreview?.filename, sourcePreviewInfo?.originalFilename]);

  // The active supplier profile is resolved generically from the fingerprint +
  // the live fuel_companies list. A company added in the DB therefore gets a
  // working preset with zero code changes (generic profile fallback).
  const activeFuelProfile = useMemo<FuelSupplierProfile | null>(
    () => resolveActiveProfile(getFuelSupplierFingerprint(), selectedTable, fuelCompanies),
    [getFuelSupplierFingerprint, selectedTable, fuelCompanies],
  );

  const activeFuelCompanyId = useMemo<number | null>(
    () => (activeFuelProfile ? resolveFuelCompanyId(activeFuelProfile, fuelCompanies) : null),
    [activeFuelProfile, fuelCompanies],
  );

  // Back-compat helpers (ImportForm still consumes isCircleKContext).
  const isCircleKContext = useCallback((): boolean => activeFuelProfile?.key === 'circle-k', [activeFuelProfile]);
  const isOkq8Context = useCallback((): boolean => activeFuelProfile?.key === 'okq8', [activeFuelProfile]);
  const isFuelDisallowedTarget = useCallback(
    (fieldName: string): boolean => !!activeFuelProfile && activeFuelProfile.disallowedTargets.includes(fieldName),
    [activeFuelProfile],
  );

  // Preset buttons: explicit profiles first (stable test ids), then one per
  // active DB company. Labels prefer the live DB name.
  const presetEntries = useMemo(
    () =>
      buildProfileList(fuelCompanies).map((p) => ({
        key: p.key,
        label:
          fuelCompanies.find((c) => p.companyNames.some((cn) => cn.toLowerCase() === c.name.toLowerCase()))?.name ||
          p.label,
      })),
    [fuelCompanies],
  );

  const buildPurchasedMapping = useCallback(
    (sourceByNormalized: Map<string, string>, dateAliases: string[], timeAliases: string[], sourceFormat = 'YYYYMMDD', timeFormat = 'HHmm'): FieldMapping | null => {
      let dateColumn: string | null = null;
      for (const alias of dateAliases) {
        const candidate = sourceByNormalized.get(normalizeHeaderName(alias));
        if (candidate) { dateColumn = candidate; break; }
      }
      if (!dateColumn) return null;

      let timeColumn: string | null = null;
      for (const alias of timeAliases) {
        const candidate = sourceByNormalized.get(normalizeHeaderName(alias));
        if (candidate) { timeColumn = candidate; break; }
      }

      return {
        id: crypto.randomUUID(),
        targetField: 'purchased',
        type: 'datetime',
        sourceColumns: timeColumn ? [dateColumn, timeColumn] : [dateColumn],
        transform: {
          sourceFormat,
          dateFormat: sourceFormat,
          ...(timeColumn ? { timeFormat, combineDateAndTime: true } : {}),
          sourceTimezone: 'Europe/Stockholm',
          targetTimezone: 'Europe/Stockholm',
        },
      };
    },
    [],
  );

  const buildCustomerNumberMapping = useCallback(
    (sourceByNormalized: Map<string, string>, aliases: string[]): FieldMapping | null => {
      for (const alias of aliases) {
        const candidate = sourceByNormalized.get(normalizeHeaderName(alias));
        if (candidate) {
          return { id: crypto.randomUUID(), targetField: 'customer_number', type: 'simple', sourceColumns: [candidate] };
        }
      }
      return null;
    },
    [],
  );

  const buildProfilePurchasedMapping = useCallback(
    (sourceByNormalized: Map<string, string>, profile: FuelSupplierProfile): FieldMapping | null =>
      profile.purchased
        ? buildPurchasedMapping(
            sourceByNormalized,
            profile.purchased.dateAliases,
            profile.purchased.timeAliases,
            profile.purchased.dateFormat,
            profile.purchased.timeFormat,
          )
        : null,
    [buildPurchasedMapping],
  );

  /** OKQ8: Account Number + Card Serial Number (serial padLeft 4 → t.ex. 5047095 + 1 → 50470950001). */
  const buildCardNumberMapping = useCallback(
    (
      sourceByNormalized: Map<string, string>,
      accountAliases: string[],
      serialAliases: string[],
      serialPadLength: number,
      existingId?: string,
    ): FieldMapping | null => {
      let accountColumn: string | null = null;
      for (const alias of accountAliases) {
        const candidate = sourceByNormalized.get(normalizeHeaderName(alias));
        if (candidate) {
          accountColumn = candidate;
          break;
        }
      }
      if (!accountColumn) return null;

      let serialColumn: string | null = null;
      for (const alias of serialAliases) {
        const candidate = sourceByNormalized.get(normalizeHeaderName(alias));
        if (candidate) {
          serialColumn = candidate;
          break;
        }
      }
      if (!serialColumn) return null;

      return {
        id: existingId || crypto.randomUUID(),
        targetField: 'card_number',
        type: 'simple',
        sourceColumns: [accountColumn, serialColumn],
        transforms: [
          {
            type: 'concat',
            params: {
              values: [`{${accountColumn}}`, `{${serialColumn}}`],
              separator: '',
              padLeft: {
                [serialColumn]: { length: serialPadLength, char: '0' },
              },
            },
          },
        ],
      };
    },
    [],
  );

  // Single generic normaliser parameterised by the active supplier profile.
  // Preserves the prior Circle K / OKQ8 / Preem output exactly while making any
  // other (incl. brand-new DB) company work via its profile.
  const normalizeFuelSupplierMappings = useCallback(
    (mappings: FieldMapping[], availableColumns?: FilePreviewColumn[]): FieldMapping[] => {
      // Alltid strippa droppade/interna fält för bränsle (även utan aktiv profil).
      const fuelBase =
        selectedTable === FUEL_TARGET_TABLE
          ? mappings.filter((m) => !FUEL_SUPPLIER_DISALLOWED_TARGETS.has(m.targetField))
          : mappings;

      const profile = activeFuelProfile;
      if (!profile) return fuelBase;

      const availableByNormalized = new Map<string, string>();
      (availableColumns || []).forEach((col) => {
        const normalized = normalizeHeaderName(col.name);
        if (normalized && !availableByNormalized.has(normalized)) availableByNormalized.set(normalized, col.name);
      });

      const normalizedMap = new Map<string, FieldMapping>();
      fuelBase
        .filter((m) => !profile.disallowedTargets.includes(m.targetField))
        .forEach((m) => normalizedMap.set(m.targetField, m));

      if (activeFuelCompanyId != null) {
        normalizedMap.set('fuel_company_id', {
          id: normalizedMap.get('fuel_company_id')?.id || crypto.randomUUID(),
          targetField: 'fuel_company_id', type: 'constant', sourceColumns: [], transform: { value: activeFuelCompanyId },
        });
      }

      if (profile.customerIdAliases.length && !normalizedMap.has('customer_number')) {
        const customerNumber = buildCustomerNumberMapping(availableByNormalized, profile.customerIdAliases);
        if (customerNumber) normalizedMap.set('customer_number', customerNumber);
      }
      normalizedMap.delete('customer_id');

      if (profile.purchased) {
        const purchased = buildProfilePurchasedMapping(availableByNormalized, profile);
        if (purchased) {
          normalizedMap.set('purchased', purchased);
        } else if (profile.purchased.reconstructFallback && normalizedMap.has('purchased')) {
          const existing = normalizedMap.get('purchased') as FieldMapping;
          const normalizedSources = (existing.sourceColumns || []).map((s) => normalizeHeaderName(s));
          const hasDate = normalizedSources.some((s) =>
            containsAnyToken(s, profile.purchased!.dateAliases.map((a) => normalizeHeaderName(a))),
          );
          const hasTime = normalizedSources.some((s) =>
            containsAnyToken(s, profile.purchased!.timeAliases.map((a) => normalizeHeaderName(a))),
          );
          normalizedMap.set('purchased', {
            ...existing,
            type: 'datetime',
            sourceColumns: existing.sourceColumns || [],
            transform: {
              sourceFormat: profile.purchased.dateFormat, dateFormat: profile.purchased.dateFormat,
              ...(hasDate && hasTime && (existing.sourceColumns || []).length >= 2
                ? { timeFormat: profile.purchased.timeFormat, combineDateAndTime: true }
                : {}),
              sourceTimezone: 'Europe/Stockholm', targetTimezone: 'Europe/Stockholm',
            },
          });
        }
      }

      if (profile.cardNumber) {
        const existingCard = normalizedMap.get('card_number');
        const cardNumber = buildCardNumberMapping(
          availableByNormalized,
          profile.cardNumber.accountAliases,
          profile.cardNumber.serialAliases,
          profile.cardNumber.serialPadLength,
          existingCard?.id,
        );
        if (cardNumber) normalizedMap.set('card_number', cardNumber);
      }

      return Array.from(normalizedMap.values());
    },
    [
      selectedTable,
      activeFuelProfile,
      activeFuelCompanyId,
      buildCustomerNumberMapping,
      buildProfilePurchasedMapping,
      buildCardNumberMapping,
    ],
  );

  // ---------- Data loaders ----------
  const loadDefinitions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/imports/definitions');
      if (response.data.success) setDefinitions(response.data.data);
    } catch {
      toast.error(t('imports.loadError') || 'Kunde inte ladda importer');
    } finally {
      setLoading(false);
    }
  };

  const loadAllowedTables = async () => {
    try {
      const response = await api.get('/api/admin/imports/tables');
      if (response.data.success) setAllowedTables(response.data.data);
    } catch { /* silent */ }
  };

  const loadImportSources = async () => {
    try {
      setImportSourcesLoading(true);
      const response = await api.get('/api/admin/import-sources');
      if (response.data.success) setImportSources(response.data.data || []);
    } catch {
      setImportSources([]);
    } finally {
      setImportSourcesLoading(false);
    }
  };

  const loadTableFields = async (tableName: string) => {
    try {
      const response = await api.get(`/api/admin/imports/tables/${tableName}/fields`);
      if (response.data.success) setTableFields(response.data.data);
    } catch { /* silent */ }
  };

  const loadFuelCompanies = async () => {
    try {
      const list = await organizationsService.getFuelCompanies();
      setFuelCompanies(list.map((c) => ({ id: c.id, name: c.name })));
    } catch { /* silent — falls back to legacy hardcoded fuel_company_id */ }
  };

  const loadHistoryRuns = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      if (historyFilterDefinitionId) params.set('definitionId', historyFilterDefinitionId);
      if (historyFilterStatus) params.set('status', historyFilterStatus);
      params.set('limit', '50');
      const response = await api.get(`/api/admin/imports/runs?${params.toString()}`);
      if (response.data.success && response.data.data) {
        setHistoryRuns(response.data.data.runs ?? []);
        setHistoryTotal(response.data.data.total ?? 0);
      }
    } catch {
      toast.error('Kunde inte ladda körningshistorik');
    } finally {
      setHistoryLoading(false);
    }
  }, [historyFilterDefinitionId, historyFilterStatus]);

  // ---------- Initial data loading ----------
  useEffect(() => {
    loadDefinitions();
    loadAllowedTables();
    loadImportSources();
    loadFuelCompanies();
  }, []);

  useEffect(() => { loadHistoryRuns(); }, [loadHistoryRuns]);

  useEffect(() => {
    if (selectedTable) loadTableFields(selectedTable);
  }, [selectedTable]);

  // ---------- File handling ----------
  const buildPreviewSettings = useCallback(() => ({
    encoding: encoding || undefined,
    sheetName: sheetName || undefined,
    sheetIndex: sheetIndex !== '' ? Number(sheetIndex) : undefined,
    csvSettings: { delimiter, hasHeader, skipRows },
    headerCells: headerCells.length > 0 ? headerCells : undefined,
    numberFormat: { decimalSeparator, thousandSeparator: decimalSeparator === ',' ? ' ' : ',', autoConvertToDot: true },
  }), [delimiter, hasHeader, skipRows, headerCells, decimalSeparator, encoding, sheetName, sheetIndex]);

  const handleFileUpload = useCallback(async (file: File) => {
    setUploadedFile(file);
    setUploading(true);
    setSourcePreviewInfo(null);
    setSourcePreviewError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('settings', JSON.stringify(buildPreviewSettings()));
      const response = await api.post('/api/admin/imports/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        setFilePreview(response.data.data);
        setSelectedFormats([String(response.data.data?.format || '').toLowerCase()].filter(Boolean));
        toast.success(t('imports.fileLoaded') || 'Fil laddad');
      } else {
        toast.error(response.data.error || 'Kunde inte läsa filen');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Uppladdningsfel');
    } finally {
      setUploading(false);
    }
  }, [buildPreviewSettings, t]);

  const loadPreviewFromSelectedSource = useCallback(async (
    sourceId: string,
    options?: { silent?: boolean; settingsOverride?: Record<string, unknown> }
  ) => {
    if (!sourceId) { setSourcePreviewInfo(null); setSourcePreviewError(null); return; }
    setSourcePreviewLoading(true);
    setSourcePreviewError(null);
    try {
      const settings = {
        ...buildPreviewSettings(),
        ...(options?.settingsOverride || {}),
      };
      const response = await api.post('/api/admin/imports/preview/source-latest', {
        sourceId: Number(sourceId), settings,
      });
      if (response.data?.success && response.data?.data) {
        setFilePreview(response.data.data as FilePreview);
        const detectedEnc = String(response.data.data?.encoding || '').trim();
        if (detectedEnc) setEncoding(detectedEnc);
        setSelectedFormats([String(response.data.data?.format || '').toLowerCase()].filter(Boolean));
        const sourceFile = response.data.data?.sourceFile;
        if (sourceFile) {
          setSourcePreviewInfo({
            id: Number(sourceFile.id), sourceId: Number(sourceFile.sourceId),
            originalFilename: sourceFile.originalFilename, fileDate: sourceFile.fileDate, createdAt: sourceFile.createdAt,
          });
        } else {
          setSourcePreviewInfo(null);
        }
        if (!options?.silent) {
          toast.success(t('imports.sourcePreviewLoaded') || 'Mallfil laddad från källa');
        }
      } else {
        throw new Error(response.data?.error || 'Source preview failed');
      }
    } catch (error: any) {
      const message = error?.response?.status === 404
        ? (t('imports.sourcePreviewNotFound') || 'Ingen synkad fil hittades för vald källa')
        : (error?.response?.data?.error || t('imports.sourcePreviewLoadError') || 'Kunde inte ladda mallfil från källa');
      setSourcePreviewInfo(null);
      setSourcePreviewError(message);
      if (!options?.silent) toast.error(message);
    } finally {
      setSourcePreviewLoading(false);
    }
  }, [buildPreviewSettings, t]);

  useEffect(() => {
    if (wizardStep !== 2) return;
    if (!selectedSourceId) { setSourcePreviewLoading(false); setSourcePreviewInfo(null); setSourcePreviewError(null); return; }
    void loadPreviewFromSelectedSource(selectedSourceId);
  }, [wizardStep, selectedSourceId, loadPreviewFromSelectedSource]);

  // Vid redigering hoppas steg 2 över — ladda mallfil så "Värde som sparas" fungerar i steg 3–4
  useEffect(() => {
    if (wizardStep !== 3 && wizardStep !== 4) return;
    if (!selectedSourceId || filePreview?.columns?.length) return;
    if (sourcePreviewLoading) return;
    void loadPreviewFromSelectedSource(selectedSourceId, {
      silent: true,
      settingsOverride: { encoding: encoding || 'ISO-8859-1' },
    });
  }, [wizardStep, selectedSourceId, filePreview?.columns?.length, sourcePreviewLoading, encoding, loadPreviewFromSelectedSource]);

  // Steg 4: bygg om bränsle-mappningar (purchased, OKQ8 card_number, …) när profil + kolumner finns.
  useEffect(() => {
    if (wizardStep !== 4) return;
    if (!activeFuelProfile || !filePreview?.columns?.length) return;
    if (!activeFuelProfile.purchased && !activeFuelProfile.cardNumber) return;
    setFieldMappings((prev) => {
      if (prev.length === 0) return prev;
      const next = normalizeFuelSupplierMappings(prev, filePreview.columns);
      const fingerprint = (maps: FieldMapping[]) =>
        maps
          .map(
            (m) =>
              `${m.targetField}|${m.type}|${(m.sourceColumns || []).join(',')}|${JSON.stringify(m.transform || {})}|${JSON.stringify(m.transforms || [])}`,
          )
          .sort()
          .join(';');
      return fingerprint(prev) === fingerprint(next) ? prev : next;
    });
  }, [wizardStep, activeFuelProfile, filePreview?.columns, normalizeFuelSupplierMappings]);

  // Auto-map source columns to target fields in step 3
  useEffect(() => {
    if (wizardStep !== 3) return;
    if (!filePreview?.columns?.length || tableFields.length === 0) return;
    if (fieldMappings.length > 0) return;

    const sourceByNormalized = new Map<string, string>();
    filePreview.columns.forEach((col) => {
      const normalized = normalizeHeaderName(col.name);
      if (normalized && !sourceByNormalized.has(normalized)) sourceByNormalized.set(normalized, col.name);
    });

    const generatedMappings: FieldMapping[] = [];
    const headerPairs = headerBindingsToFieldPairs(headerCells);
    const headerMappedTargets = new Set(headerPairs.map((p) => p.targetField));
    for (const pair of headerPairs) {
      if (!tableFields.some((f) => f.name === pair.targetField)) continue;
      if (activeFuelProfile?.disallowedTargets.includes(pair.targetField)) continue;
      generatedMappings.push({
        id: crypto.randomUUID(),
        targetField: pair.targetField,
        type: 'simple',
        sourceColumns: [pair.sourceColumn],
      });
    }

    tableFields.forEach((field) => {
      if (headerMappedTargets.has(field.name)) return;
      if (activeFuelProfile && activeFuelProfile.disallowedTargets.includes(field.name)) return;
      const normalizedTarget = normalizeHeaderName(field.name);
      let matchedSource = sourceByNormalized.get(normalizedTarget);

      if (!matchedSource) {
        const aliases = AUTO_MAP_TARGET_ALIASES[field.name] || [];
        for (const alias of aliases) {
          const candidate = sourceByNormalized.get(normalizeHeaderName(alias));
          if (candidate) { matchedSource = candidate; break; }
        }
      }

      if (activeFuelProfile && field.name === 'fuel_company_id') {
        if (activeFuelCompanyId != null) {
          generatedMappings.push({ id: crypto.randomUUID(), targetField: field.name, type: 'constant', sourceColumns: [], transform: { value: activeFuelCompanyId } });
        }
        return;
      }
      if (activeFuelProfile && activeFuelProfile.customerIdAliases.length && field.name === 'customer_number') {
        const cn = buildCustomerNumberMapping(sourceByNormalized, activeFuelProfile.customerIdAliases);
        if (cn) generatedMappings.push(cn);
        return;
      }
      if (activeFuelProfile && activeFuelProfile.purchased && field.name === 'purchased') {
        const p = buildProfilePurchasedMapping(sourceByNormalized, activeFuelProfile);
        if (p) generatedMappings.push(p);
        return;
      }

      if (matchedSource) {
        const columnMeta = filePreview.columns.find((col) => col.name === matchedSource);
        const sampleValue = String(columnMeta?.sampleValues?.[0] || '').trim();
        const isDecimalTarget = ['quantity', 'price_excl_vat', 'vat'].includes(field.name);
        // Preem: belopp/volym utan decimaltecken (öre / centiliter-liknande) → /100
        const isPreemScaledDecimal =
          activeFuelProfile?.key === 'preem' &&
          (field.name === 'price_excl_vat' || field.name === 'vat' || field.name === 'quantity');
        generatedMappings.push({
          id: crypto.randomUUID(), targetField: field.name,
          type: isDecimalTarget ? 'decimal' : 'simple', sourceColumns: [matchedSource],
          ...((activeFuelProfile?.key === 'circle-k' || activeFuelProfile?.key === 'preem') &&
          field.name === 'invoice_date' &&
          /^\d{8}$/.test(sampleValue)
            ? { transforms: [{ type: 'date_parse', params: { inputFormat: 'YYYYMMDD', outputFormat: 'YYYY-MM-DD' } }] }
            : {}),
          ...(isPreemScaledDecimal
            ? {
                transforms: [
                  { type: 'divide', params: { value: 100 } },
                  { type: 'round', params: { decimals: 2 } },
                ],
              }
            : {}),
        });
      }
    });

    if (generatedMappings.length > 0) {
      setFieldMappings(normalizeFuelSupplierMappings(generatedMappings, filePreview.columns));
      toast.success(`${generatedMappings.length} ${t('imports.autoMappedFields') || 'fält mappades automatiskt'}`);
    }
  }, [
    wizardStep, filePreview, tableFields, fieldMappings.length, t,
    headerCells, activeFuelProfile, activeFuelCompanyId,
    buildCustomerNumberMapping, buildProfilePurchasedMapping,
    normalizeFuelSupplierMappings,
  ]);

  const syncFieldMappingsFromHeaderCells = useCallback((bindings: HeaderCellBinding[]) => {
    const pairs = headerBindingsToFieldPairs(bindings);
    const virtualCols = new Set(headerBindingColumnNames(bindings));
    setFieldMappings((prev) => {
      const kept = prev.filter((m) => {
        const src = m.sourceColumns?.[0];
        if (!src) return true;
        // Ta bort gamla header-mappningar (Header * eller tidigare virtuella kolumner)
        if (src.startsWith('Header ')) return false;
        if (virtualCols.has(src) && !pairs.some((p) => p.sourceColumn === src && p.targetField === m.targetField)) {
          return false;
        }
        // Om samma targetField mappas om via headerCells, ersätt
        if (pairs.some((p) => p.targetField === m.targetField)) return false;
        return true;
      });
      const added: FieldMapping[] = pairs.map((p) => ({
        id: crypto.randomUUID(),
        targetField: p.targetField,
        type: 'simple',
        sourceColumns: [p.sourceColumn],
      }));
      return [...kept, ...added];
    });
  }, []);

  const headerCellsRef = useRef<HeaderCellBinding[]>([]);
  headerCellsRef.current = headerCells;

  const applyHeaderCellsState = useCallback((next: HeaderCellBinding[]) => {
    setHeaderCells(next);
    setFilePreview((fp) => (fp?.preambleRows?.length ? applyHeaderCellsToPreview(fp, next) : fp));
    syncFieldMappingsFromHeaderCells(next);
  }, [syncFieldMappingsFromHeaderCells]);

  const upsertHeaderCell = useCallback((binding: HeaderCellBinding) => {
    const next = [
      ...headerCellsRef.current.filter((b) => b.cell.toUpperCase() !== binding.cell.toUpperCase()),
      binding,
    ];
    applyHeaderCellsState(next);
  }, [applyHeaderCellsState]);

  const removeHeaderCell = useCallback((cell: string) => {
    applyHeaderCellsState(headerCellsRef.current.filter((b) => b.cell.toUpperCase() !== cell.toUpperCase()));
  }, [applyHeaderCellsState]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  // ---------- Mapping management ----------
  const addMapping = (sourceColumn: string, targetField: string) => {
    setFieldMappings([...fieldMappings, {
      id: crypto.randomUUID(), targetField, type: 'simple', sourceColumns: [sourceColumn],
    }]);
  };

  const removeMapping = (id: string) => {
    setFieldMappings(fieldMappings.filter((m) => m.id !== id));
    setTransformParamsText((prev) => {
      const next: Record<string, string> = {};
      Object.keys(prev).forEach((key) => { if (!key.startsWith(`${id}:`)) next[key] = prev[key]; });
      return next;
    });
  };

  const updateMapping = (id: string, updater: (mapping: FieldMapping) => FieldMapping) => {
    setFieldMappings((prev) => prev.map((m) => (m.id === id ? updater(m) : m)));
  };

  const addTransformStep = (mappingId: string) => {
    updateMapping(mappingId, (mapping) => ({
      ...mapping, transforms: [...(mapping.transforms || []), { type: 'trim', params: {} }],
    }));
  };

  const removeTransformStep = (mappingId: string, index: number) => {
    updateMapping(mappingId, (mapping) => ({
      ...mapping, transforms: (mapping.transforms || []).filter((_, i) => i !== index),
    }));
    setTransformParamsText((prev) => { const next = { ...prev }; delete next[`${mappingId}:${index}`]; return next; });
  };

  const moveTransformStep = (mappingId: string, index: number, direction: -1 | 1) => {
    updateMapping(mappingId, (mapping) => {
      const transforms = [...(mapping.transforms || [])];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= transforms.length) return mapping;
      const [item] = transforms.splice(index, 1);
      transforms.splice(targetIndex, 0, item);
      return { ...mapping, transforms };
    });
    setTransformParamsText((prev) => {
      const currentKey = `${mappingId}:${index}`;
      const targetKey = `${mappingId}:${index + direction}`;
      if (!(currentKey in prev) && !(targetKey in prev)) return prev;
      const next = { ...prev };
      const currentValue = next[currentKey];
      const targetValue = next[targetKey];
      if (currentValue !== undefined) next[targetKey] = currentValue; else delete next[targetKey];
      if (targetValue !== undefined) next[currentKey] = targetValue; else delete next[currentKey];
      return next;
    });
  };

  const updateTransformType = (mappingId: string, index: number, type: string) => {
    updateMapping(mappingId, (mapping) => {
      const transforms = [...(mapping.transforms || [])];
      transforms[index] = { ...transforms[index], type };
      return { ...mapping, transforms };
    });
  };

  const getTransformParamsText = (mappingId: string, index: number, params?: Record<string, any>) => {
    const key = `${mappingId}:${index}`;
    if (transformParamsText[key] !== undefined) return transformParamsText[key];
    return JSON.stringify(params || {}, null, 2);
  };

  const handleTransformParamsChange = (mappingId: string, index: number, value: string) => {
    setTransformParamsText((prev) => ({ ...prev, [`${mappingId}:${index}`]: value }));
  };

  const handleTransformParamsBlur = (mappingId: string, index: number) => {
    const key = `${mappingId}:${index}`;
    const raw = transformParamsText[key];
    if (raw === undefined) return;
    if (!raw.trim()) {
      updateMapping(mappingId, (mapping) => {
        const transforms = [...(mapping.transforms || [])];
        transforms[index] = { ...transforms[index], params: {} };
        return { ...mapping, transforms };
      });
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      updateMapping(mappingId, (mapping) => {
        const transforms = [...(mapping.transforms || [])];
        transforms[index] = { ...transforms[index], params: parsed };
        return { ...mapping, transforms };
      });
    } catch {
      toast.error('Ogiltig JSON i transform-parametrar');
    }
  };

  // ---------- Save / Delete ----------
  const saveDefinition = async () => {
    if (!importName || !selectedTable || fieldMappings.length === 0) {
      toast.error(t('imports.missingFields') || 'Fyll i alla obligatoriska fält');
      return;
    }
    setSchemaValidationErrors([]);
    try {
      const allowedTargetNames = new Set(tableFields.map((f) => f.name));
      const sanitizedMappings = normalizeFuelSupplierMappings(fieldMappings, filePreview?.columns).filter(
        (m) => allowedTargetNames.size === 0 || allowedTargetNames.has(m.targetField)
      );
      if (sanitizedMappings.length !== fieldMappings.length) {
        setFieldMappings(sanitizedMappings);
      }
      const defaultFormats = selectedFormats && selectedFormats.length > 0
        ? selectedFormats
        : [String(filePreview?.format || 'csv').toLowerCase()];
      const allowedFormats = activeFuelProfile?.acceptFormats ?? defaultFormats;
      const requiredFields = allowedTables.find((tbl) => tbl.tableName === selectedTable)?.requiredFields;
      const validateRes = await api.post('/api/admin/imports/schema/validate', {
        targetTable: selectedTable, fieldMappings: sanitizedMappings, requiredFields: requiredFields ?? [],
      });
      const { valid, errors } = validateRes.data?.data ?? { valid: true, errors: [] };
      if (!valid && errors?.length) {
        setSchemaValidationErrors(errors);
        toast.error(errors[0]?.message || 'Schema-validering misslyckades');
        return;
      }

      const definition = {
        name: importName, description: importDescription, targetTable: selectedTable,
        sourceId: selectedSourceId ? Number(selectedSourceId) : null,
        fileSettings: {
          format: allowedFormats.length === 1 ? allowedFormats[0] : allowedFormats,
          encoding: encoding || filePreview?.encoding || 'UTF-8',
          sheetName: sheetName || undefined,
          sheetIndex: sheetIndex !== '' ? Number(sheetIndex) : undefined,
          numberFormat: { decimalSeparator, thousandSeparator: decimalSeparator === ',' ? ' ' : ',', autoConvertToDot: true },
          datetimeSettings: { sourceFormat: 'ISO8601', sourceTimezone: 'Europe/Stockholm', convertToUtc: true },
          csvSettings: { delimiter, quoteChar: '"', hasHeader, skipRows },
          headerCells: headerCells.length > 0 ? headerCells : undefined,
          filenameIncludePatterns:
            filenameIncludePatterns.map((p) => String(p || '').trim()).filter(Boolean).length > 0
              ? filenameIncludePatterns.map((p) => String(p || '').trim()).filter(Boolean)
              : undefined,
        },
        fieldMappings: sanitizedMappings,
        importSettings: {
          duplicateAction,
          duplicateKeyFields: allowedTables.find((tbl) => tbl.tableName === selectedTable)?.uniqueKeyFields || [],
          errorAction,
          validation: { checkRequired: true, checkDataTypes: true, checkUnique: true, checkForeignKeys: true },
          batchSize: 100,
        },
        scheduleType,
        scheduleConfig: scheduleType === 'manual' ? undefined : {
          type: scheduleType, time: scheduleTime || '06:00',
          dayOfWeek: scheduleType === 'weekly' ? scheduleDayOfWeek : undefined,
          dayOfMonth: scheduleType === 'monthly' ? scheduleDayOfMonth : undefined,
          cron: scheduleCron || undefined,
          fileSourcePath: fileSourcePath.trim() || undefined,
          stableFile: fileSourcePath.trim() && (stableFileStableForMs > 0 || stableFileTimeoutMs > 0)
            ? { checkIntervalMs: stableFileCheckIntervalMs, stableForMs: stableFileStableForMs, timeoutMs: stableFileTimeoutMs > 0 ? stableFileTimeoutMs : undefined }
            : undefined,
          retry: fileSourcePath.trim() && retryMaxRetries > 1
            ? { maxRetries: retryMaxRetries, initialDelayMs: retryInitialDelayMs, maxDelayMs: retryMaxDelayMs, backoffMultiplier: retryBackoffMultiplier }
            : undefined,
        },
      };

      const response = editingId
        ? await api.put(`/api/admin/imports/definitions/${editingId}`, definition)
        : await api.post('/api/admin/imports/definitions', definition);

      if (response.data.success) {
        setSchemaValidationErrors([]);
        toast.success(editingId ? 'Import uppdaterad' : (t('imports.saved') || 'Import sparad'));
        setShowWizard(false);
        resetWizard();
        loadDefinitions();
      }
    } catch (error: any) {
      const errData = error.response?.data;
      if (errData?.data?.errors?.length) setSchemaValidationErrors(errData.data.errors);
      toast.error(errData?.error || 'Kunde inte spara');
    }
  };

  const deleteDefinition = async (id: number) => {
    if (!confirm(t('imports.confirmDelete') || 'Vill du verkligen radera denna import?')) return;
    try {
      await api.delete(`/api/admin/imports/definitions/${id}`);
      toast.success(t('imports.deleted') || 'Import raderad');
      loadDefinitions();
    } catch {
      toast.error(t('common.deleteError'));
    }
  };

  // ---------- Run import ----------
  const handleRunImportFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const definitionId = runImportDefinitionId;
    if (!file || !definitionId) return;
    const isDryRun = runImportDryRun;
    const rowLimit = runImportRowLimit;

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (isDryRun) formData.append('dryRun', 'true');
      if (rowLimit && rowLimit > 0) formData.append('rowLimit', String(rowLimit));

      const response = await api.post(`/api/admin/imports/definitions/${definitionId}/run`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'X-Import-Progress': 'sse' },
      });

      if (response.status === 202 && response.data?.data?.runId) {
        const runId = response.data.data.runId;
        setImportProgress({ runId, processed: 0, total: 0, imported: 0, updated: 0, skipped: 0, failed: 0 });
        const base = (api.defaults.baseURL as string) || '';
        const sseUrl = `${base}/api/admin/imports/runs/${runId}/progress`;
        const eventSource = new EventSource(sseUrl, { withCredentials: true });
        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            setImportProgress((prev) => (prev ? { ...prev, ...data } : null));
            if (data.status === 'complete' && data.run) {
              const run = data.run;
              toast.success(isDryRun
                ? `Testimport klar: ${run.rowsProcessed} rader testade, ${run.rowsFailed} misslyckade (ingen data sparad)`
                : `Import klar: ${run.rowsImported} importerade, ${run.rowsUpdated} uppdaterade, ${run.rowsFailed} misslyckade`);
              loadDefinitions();
              loadHistoryRuns();
              eventSource.close();
              setImportProgress(null);
            }
            if (data.status === 'error') {
              toast.error(data.error || 'Import misslyckades');
              eventSource.close();
              setImportProgress(null);
              loadHistoryRuns();
            }
          } catch { /* ignore parse errors */ }
        };
        eventSource.onerror = () => {
          eventSource.close();
          setImportProgress(null);
          setRunImportDefinitionId(null);
          setRunImportDryRun(false);
          setRunImportRowLimit(null);
          loadHistoryRuns();
        };
        return;
      }

      if (response.data.success) {
        const run = response.data.data;
        toast.success(isDryRun
          ? `Testimport klar: ${run.rowsProcessed} rader testade, ${run.rowsFailed} misslyckade (ingen data sparad)`
          : `Import klar: ${run.rowsImported} importerade, ${run.rowsUpdated} uppdaterade, ${run.rowsFailed} misslyckade`);
        loadDefinitions();
        loadHistoryRuns();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Import misslyckades');
      setImportProgress(null);
    } finally {
      if (runImportInputRef.current) runImportInputRef.current.value = '';
      setRunImportDefinitionId(null);
      setRunImportDryRun(false);
      setRunImportRowLimit(null);
    }
  };

  const runImport = async (definitionId: number, options?: { dryRun?: boolean; rowLimit?: number }) => {
    setRunImportDefinitionId(definitionId);
    setRunImportDryRun(!!options?.dryRun);
    setRunImportRowLimit(options?.rowLimit ?? null);
    runImportInputRef.current?.click();
  };

  // ---------- FTP ----------
  const loadFtpFiles = async (pathOverride?: string) => {
    const targetPath = pathOverride !== undefined ? pathOverride : ftpPath;
    setFtpLoading(true);
    setFtpError(null);
    try {
      const response = await api.get('/api/admin/imports/ftp/list', {
        params: targetPath ? { path: targetPath } : undefined,
      });
      if (response.data.success) {
        setFtpFiles(response.data.data.files || []);
        setFtpPath(response.data.data.path || targetPath || '');
      } else {
        setFtpError(response.data.error || 'Kunde inte lista FTP-filer');
      }
    } catch (error: any) {
      setFtpError(error.response?.data?.error || 'Kunde inte lista FTP-filer');
    } finally {
      setFtpLoading(false);
    }
  };

  const openFtpModal = async (definitionId: number) => {
    setFtpDefinitionId(definitionId);
    setShowFtpModal(true);
    await loadFtpFiles();
  };

  const closeFtpModal = () => {
    setShowFtpModal(false);
    setFtpFiles([]);
    setFtpPath('');
    setFtpError(null);
    setFtpDefinitionId(null);
  };

  const runFtpImport = async (remotePath: string) => {
    if (!ftpDefinitionId) return;
    setFtpLoading(true);
    setFtpError(null);
    try {
      const response = await api.post(`/api/admin/imports/definitions/${ftpDefinitionId}/ftp/run`, { path: remotePath });
      if (response.data.success) {
        const run = response.data.data;
        toast.success(`Import klar: ${run.rowsImported} importerade, ${run.rowsUpdated} uppdaterade, ${run.rowsFailed} misslyckade`);
        closeFtpModal();
        loadDefinitions();
      } else {
        setFtpError(response.data.error || 'FTP-import misslyckades');
      }
    } catch (error: any) {
      setFtpError(error.response?.data?.error || 'FTP-import misslyckades');
    } finally {
      setFtpLoading(false);
    }
  };

  // ---------- Source files ----------
  const openSourceFilesModal = async (def: ImportDefinition) => {
    if (def.sourceId == null || def.sourceId === undefined) return;
    setSourceFilesModal({ definitionId: def.id, definitionName: def.name, sourceId: def.sourceId });
    setSourceFilesList([]);
    setSourceFilesLoading(true);
    try {
      const response = await api.get(`/api/admin/import-sources/${def.sourceId}/files`, {
        params: { limit: 50, offset: 0, sortBy: 'file_date', sortDir: 'desc' },
      });
      if (response.data.success && response.data.data?.files) setSourceFilesList(response.data.data.files);
    } catch {
      toast.error('Kunde inte ladda filer från källan');
    } finally {
      setSourceFilesLoading(false);
    }
  };

  const closeSourceFilesModal = () => {
    setSourceFilesModal(null);
    setSourceFilesList([]);
    setSourceFilesImportingId(null);
  };

  const runImportFromSourceFile = async (definitionId: number, fileId: number) => {
    setSourceFilesImportingId(fileId);
    try {
      const response = await api.post(`/api/admin/imports/definitions/${definitionId}/run-source-file`, { fileId });
      if (response.data.success) {
        const run = response.data.data;
        toast.success(`Import klar: ${run.rowsImported} importerade, ${run.rowsUpdated} uppdaterade, ${run.rowsFailed} misslyckade`);
        loadHistoryRuns();
        loadDefinitions();
      } else {
        toast.error(response.data.error || 'Import misslyckades');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Import misslyckades');
    } finally {
      setSourceFilesImportingId(null);
    }
  };

  const runImportFromLatestSource = async (definitionId: number) => {
    setSourceLatestImportingDefinitionId(definitionId);
    try {
      const response = await api.post(`/api/admin/imports/definitions/${definitionId}/run-latest-source`);
      if (response.data.success) {
        const run = response.data.data;
        toast.success(`Import klar: ${run.rowsImported} importerade, ${run.rowsUpdated} uppdaterade, ${run.rowsFailed} misslyckade`);
        loadHistoryRuns();
        loadDefinitions();
      } else {
        toast.error(response.data.error || 'Import från senaste källfil misslyckades');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Import från senaste källfil misslyckades');
    } finally {
      setSourceLatestImportingDefinitionId(null);
    }
  };

  const runImportPendingFromSource = async (definitionId: number) => {
    setSourcePendingImportingDefinitionId(definitionId);
    try {
      const response = await api.post(`/api/admin/imports/definitions/${definitionId}/run-pending-source`);
      if (response.data.success) {
        const result = response.data.data;
        const msg =
          response.data.message ||
          `${t('imports.pendingImportDone')}: ${result?.importedFiles ?? 0}/${result?.total ?? 0}`;
        if ((result?.failedFiles ?? 0) > 0) {
          toast.error(msg);
        } else {
          toast.success(msg);
        }
        loadHistoryRuns();
        loadDefinitions();
        if (sourceFilesModal?.definitionId === definitionId && sourceFilesModal.sourceId != null) {
          try {
            const filesRes = await api.get(`/api/admin/import-sources/${sourceFilesModal.sourceId}/files`, {
              params: { limit: 100, offset: 0, sortBy: 'file_date', sortDir: 'desc' },
            });
            if (filesRes.data.success && filesRes.data.data?.files) {
              setSourceFilesList(filesRes.data.data.files);
            }
          } catch {
            // ignore refresh errors
          }
        }
      } else {
        toast.error(response.data.error || t('imports.pendingImportFailed'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('imports.pendingImportFailed'));
    } finally {
      setSourcePendingImportingDefinitionId(null);
    }
  };

  // ---------- Presets ----------
  // Registry-driven: resolves the profile (explicit or generic-from-DB) by key.
  // Adding a fuel company in the DB therefore yields a working preset with no code.
  const applyPreset = (presetKey: string) => {
    setSelectedPreset(presetKey);
    setSelectedTable('fuel_transactions');
    const profile = findProfileByKey(presetKey, fuelCompanies);
    if (profile) {
      setDelimiter(profile.file.delimiter);
      setHasHeader(profile.file.hasHeader);
      setSkipRows(profile.file.skipRows ?? 0);
      applyHeaderCellsState(profile.file.headerCells ? [...profile.file.headerCells] : []);
      setDecimalSeparator(profile.file.decimalSeparator);
      setEncoding(profile.file.encoding);
      if (profile.file.formats) setSelectedFormats(profile.file.formats);
      const label =
        fuelCompanies.find((c) => profile.companyNames.some((cn) => cn.toLowerCase() === c.name.toLowerCase()))?.name ||
        profile.label;
      setImportName(`${label} Bränsleimport`);
      setImportDescription(profile.description);
      setFilenameIncludePatterns([]);
    }
    if (allowedTables.find((tbl) => tbl.tableName === 'fuel_transactions')) loadTableFields('fuel_transactions');
  };

  const clearPreset = () => { setSelectedPreset(''); };

  const resetWizard = () => {
    setWizardStep(1); setImportName(''); setImportDescription('');
    setSelectedTable(''); setSelectedSourceId(''); setTableFields([]);
    setFilePreview(null); setSelectedFormats(null);
    setSourcePreviewInfo(null); setSourcePreviewLoading(false); setSourcePreviewError(null);
    setFieldMappings([]); setUploadedFile(null); setTransformParamsText({});
    setEditingId(null); setDecimalSeparator(','); setHasHeader(true);
    setDelimiter(';'); setSkipRows(0); setHeaderCells([]); setFilenameIncludePatterns([]);
    setEncoding('UTF-8'); setSheetName(''); setSheetIndex('');
    setDuplicateAction('skip'); setErrorAction('skip');
    setScheduleType('manual'); setScheduleTime('06:00');
    setScheduleDayOfWeek(1); setScheduleDayOfMonth(1);
    setScheduleCron(''); setFileSourcePath('');
    setStableFileCheckIntervalMs(2000); setStableFileStableForMs(5000); setStableFileTimeoutMs(300000);
    setRetryMaxRetries(3); setRetryInitialDelayMs(60000); setRetryMaxDelayMs(600000);
    setRetryBackoffMultiplier(2); setSelectedPreset(''); setSchemaValidationErrors([]);
  };

  const loadDefinitionForEdit = async (id: number) => {
    try {
      const response = await api.get(`/api/admin/imports/definitions/${id}`);
      if (response.data.success) {
        const def = response.data.data;
        setEditingId(def.id);
        setImportName(def.name);
        setImportDescription(def.description || '');
        setSelectedTable(def.targetTable);
        setSelectedSourceId(def.sourceId !== undefined && def.sourceId !== null ? String(def.sourceId) : '');
        const loadedMappings = Array.isArray(def.fieldMappings) ? def.fieldMappings : [];
        setFieldMappings(normalizeFuelSupplierMappings(loadedMappings));

        if (def.fileSettings) {
          const loadedFormat = def.fileSettings.format;
          if (Array.isArray(loadedFormat)) setSelectedFormats(loadedFormat.map((f: string) => String(f).toLowerCase()).filter(Boolean));
          else if (typeof loadedFormat === 'string' && loadedFormat.trim()) setSelectedFormats([loadedFormat.toLowerCase()]);
          else setSelectedFormats(null);
          setDecimalSeparator(def.fileSettings.numberFormat?.decimalSeparator || ',');
          setHasHeader(def.fileSettings.csvSettings?.hasHeader !== false);
          setDelimiter(def.fileSettings.csvSettings?.delimiter || ';');
          setSkipRows(Number(def.fileSettings.csvSettings?.skipRows) || 0);
          setHeaderCells(Array.isArray(def.fileSettings.headerCells) ? def.fileSettings.headerCells : []);
          {
            const fromNew = Array.isArray(def.fileSettings.filenameIncludePatterns)
              ? def.fileSettings.filenameIncludePatterns.map((p: string) => String(p || '').trim()).filter(Boolean)
              : [];
            const fromLegacy = Array.isArray(def.fileSettings.filenameMappingRules)
              ? def.fileSettings.filenameMappingRules
                  .map((r: { pattern?: string }) => String(r?.pattern || '').trim())
                  .filter(Boolean)
              : [];
            setFilenameIncludePatterns(fromNew.length > 0 ? fromNew : fromLegacy);
          }
          setEncoding(def.fileSettings.encoding || 'UTF-8');
          setSheetName(def.fileSettings.sheetName || '');
          setSheetIndex(def.fileSettings.sheetIndex !== undefined && def.fileSettings.sheetIndex !== null ? String(def.fileSettings.sheetIndex) : '');
        }

        if (def.importSettings) {
          setDuplicateAction(def.importSettings.duplicateAction || 'skip');
          setErrorAction(def.importSettings.errorAction || 'skip');
        }

        setScheduleType(def.scheduleType || 'manual');
        if (def.scheduleConfig) {
          setScheduleTime(def.scheduleConfig.time || '06:00');
          setScheduleDayOfWeek(def.scheduleConfig.dayOfWeek ?? 1);
          setScheduleDayOfMonth(def.scheduleConfig.dayOfMonth ?? 1);
          setScheduleCron(def.scheduleConfig.cron || '');
          setFileSourcePath(def.scheduleConfig.fileSourcePath || '');
          if (def.scheduleConfig.stableFile) {
            setStableFileCheckIntervalMs(def.scheduleConfig.stableFile.checkIntervalMs ?? 2000);
            setStableFileStableForMs(def.scheduleConfig.stableFile.stableForMs ?? 5000);
            setStableFileTimeoutMs(def.scheduleConfig.stableFile.timeoutMs ?? 300000);
          }
          if (def.scheduleConfig.retry) {
            setRetryMaxRetries(def.scheduleConfig.retry.maxRetries ?? 3);
            setRetryInitialDelayMs(def.scheduleConfig.retry.initialDelayMs ?? 60000);
            setRetryMaxDelayMs(def.scheduleConfig.retry.maxDelayMs ?? 600000);
            setRetryBackoffMultiplier(def.scheduleConfig.retry.backoffMultiplier ?? 2);
          }
        }

        await loadTableFields(def.targetTable);
        setFilePreview(null);
        setSourcePreviewInfo(null);
        setWizardStep(3);
        setShowWizard(true);
        const sourceId = def.sourceId !== undefined && def.sourceId !== null ? String(def.sourceId) : '';
        if (sourceId) {
          // Skicka encoding från definition direkt — setEncoding är asynkron och
          // får annars preview att läsas som UTF-8 (→ � i å/ä/ö + trasiga kolumnnamn).
          void loadPreviewFromSelectedSource(sourceId, {
            silent: true,
            settingsOverride: {
              encoding: def.fileSettings?.encoding || 'ISO-8859-1',
              format: def.fileSettings?.format,
              csvSettings: def.fileSettings?.csvSettings,
              numberFormat: def.fileSettings?.numberFormat,
              headerCells: def.fileSettings?.headerCells,
            },
          });
        }
      }
    } catch {
      toast.error(t('imports.loadForEditError'));
    }
  };

  // ---------- Live preview for transforms ----------
  const selectedSource = importSources.find((source) => String(source.id) === selectedSourceId);

  const buildSampleRow = (): Record<string, string> => {
    const row: Record<string, string> = {};
    filePreview?.columns.forEach((col) => {
      if (col.sampleValues && col.sampleValues.length > 0) {
        row[col.name] = String(col.sampleValues[0] ?? '');
      }
    });
    // Fallback: första preview-raden om sampleValues saknas
    const first = filePreview?.previewRows?.[0];
    if (first && typeof first === 'object') {
      Object.entries(first).forEach(([key, val]) => {
        if (row[key] === undefined || row[key] === '') {
          row[key] = val == null ? '' : String(val);
        }
      });
    }
    return row;
  };

  const getLivePreviewValue = (mapping: FieldMapping) => {
    const row = buildSampleRow();
    const preview = previewMappedValueAsSaved(mapping, row, decimalSeparator);
    if (preview === null || preview === undefined) return '';
    return preview;
  };

  return {
    // Core
    definitions, loading,
    showWizard, setShowWizard,
    wizardStep, setWizardStep,
    editingId,

    // Wizard fields
    importName, setImportName,
    importDescription, setImportDescription,
    selectedTable, setSelectedTable,
    allowedTables,
    importSources, importSourcesLoading,
    selectedSourceId, setSelectedSourceId,
    tableFields,
    filePreview, setFilePreview,
    selectedFormats, setSelectedFormats,
    sourcePreviewInfo, setSourcePreviewInfo,
    sourcePreviewLoading, sourcePreviewError,
    fieldMappings, setFieldMappings,
    uploading,
    transformParamsText,
    selectedPreset, setSelectedPreset,
    schemaValidationErrors,

    // File settings
    decimalSeparator, setDecimalSeparator,
    hasHeader, setHasHeader,
    delimiter, setDelimiter,
    skipRows, setSkipRows,
    headerCells, upsertHeaderCell, removeHeaderCell,
    filenameIncludePatterns, setFilenameIncludePatterns,
    encoding, setEncoding,
    sheetName, setSheetName,
    sheetIndex, setSheetIndex,

    // Import settings
    duplicateAction, setDuplicateAction,
    errorAction, setErrorAction,

    // Schedule
    scheduleType, setScheduleType,
    scheduleTime, setScheduleTime,
    scheduleDayOfWeek, setScheduleDayOfWeek,
    scheduleDayOfMonth, setScheduleDayOfMonth,
    scheduleCron, setScheduleCron,
    fileSourcePath, setFileSourcePath,
    stableFileCheckIntervalMs, setStableFileCheckIntervalMs,
    stableFileStableForMs, setStableFileStableForMs,
    stableFileTimeoutMs, setStableFileTimeoutMs,
    retryMaxRetries, setRetryMaxRetries,
    retryInitialDelayMs, setRetryInitialDelayMs,
    retryMaxDelayMs, setRetryMaxDelayMs,
    retryBackoffMultiplier, setRetryBackoffMultiplier,

    // FTP modal
    showFtpModal, ftpPath, setFtpPath,
    ftpFiles, ftpLoading, ftpError,

    // Source files modal
    sourceFilesModal, sourceFilesList, sourceFilesLoading,
    sourceFilesImportingId, sourceLatestImportingDefinitionId, sourcePendingImportingDefinitionId,

    // History
    historyRuns, historyTotal, historyLoading,
    historyFilterDefinitionId, setHistoryFilterDefinitionId,
    historyFilterStatus, setHistoryFilterStatus,
    detailRunId, setDetailRunId,
    detailRun, setDetailRun,

    // Import progress
    importProgress,

    // Refs
    runImportInputRef,

    // Computed
    selectedSource,
    isCircleKContext,
    isOkq8Context,
    isFuelDisallowedTarget,
    presetEntries,

    // Actions
    loadDefinitions, loadHistoryRuns,
    handleFileUpload, handleDrop, handleFileSelect,
    loadPreviewFromSelectedSource,
    addMapping, removeMapping,
    addTransformStep, removeTransformStep, moveTransformStep,
    updateTransformType, getTransformParamsText,
    handleTransformParamsChange, handleTransformParamsBlur,
    saveDefinition, handleRunImportFileSelect,
    runImport,
    loadFtpFiles, openFtpModal, closeFtpModal, runFtpImport,
    openSourceFilesModal, closeSourceFilesModal,
    runImportFromSourceFile, runImportFromLatestSource, runImportPendingFromSource,
    deleteDefinition,
    applyPreset, clearPreset, resetWizard, loadDefinitionForEdit,
    getLivePreviewValue,
  };
};

export type UseImportsReturn = ReturnType<typeof useImportsManagement>;
